<template>
  <SfDrawer
    :open="store.isOpen"
    :title="title"
    :close-on-escape="!confirmingToggle && !confirmingDiscard"
    @close="requestClose"
  >
    <template #header>
      <SfStaticPill
        v-if="store.userStatus"
        :variant="STATUS_VARIANTS[store.userStatus]"
      >
        {{ I18n.t(`user.table.status.${store.userStatus}`) }}
      </SfStaticPill>
    </template>

    <div
      v-if="store.status === 'loading'"
      class="tw-flex tw-flex-col tw-gap-4"
      data-testid="user-panel-loading"
    >
      <SfSkeletonLoader
        v-for="placeholder in PANEL_SKELETON_ROWS"
        :key="placeholder"
        width="100%"
        height="4rem"
      />
    </div>

    <div
      v-else-if="store.status === 'error'"
      class="tw-flex tw-flex-col tw-items-start tw-gap-4"
      role="alert"
      data-testid="user-panel-error"
    >
      <p class="tw-m-0 tw-text-sm tw-text-sf-slate-dark-1">
        {{ I18n.t('user.panel.load_error') }}
      </p>
      <SfButtonDs variant="secondary" size="sm" @click="store.retry()">
        {{ I18n.t('user.panel.retry') }}
      </SfButtonDs>
    </div>

    <div v-else class="tw-flex tw-flex-col tw-gap-4">
      <ul
        v-if="store.errorMessages.length"
        class="
          tw-m-0
          tw-list-none
          tw-rounded-lg
          tw-border
          tw-border-solid
          tw-border-sf-danger-dark-1/20
          tw-bg-sf-danger-subtle-1
          tw-p-4
          tw-text-sm
          tw-text-sf-danger-dark-1
        "
        role="alert"
        data-testid="user-panel-field-errors"
      >
        <li v-for="message in store.errorMessages" :key="message">
          {{ message }}
        </li>
      </ul>

      <UserDetailsSection />

      <p
        v-if="deactivated"
        class="
          tw-m-0
          tw-rounded-lg
          tw-bg-sf-slate-subtle-1
          tw-py-10
          tw-text-center
          tw-text-sm
          tw-text-sf-slate-light-2
        "
        data-testid="user-panel-no-access"
      >
        {{ I18n.t('user.inactive_has_no_access') }}
      </p>

      <RolesSection />
      <ExternalRolesSection />
      <AuthenticationSection />
      <EmailAddressSection />
      <FileAccessSection />
      <PulseAccessSection />
    </div>

    <template #footer>
      <!-- A disabled button takes no focus, so the wrapper does, to give a
           keyboard the reason. -->
      <div
        v-if="isPersisted"
        v-tooltip="blockedReason ?? ''"
        class="tw-mr-auto"
        :tabindex="blockedReason ? 0 : undefined"
        data-testid="user-panel-toggle-active-wrapper"
      >
        <SfButtonDs
          variant="danger"
          :disabled="!!blockedReason || store.busy"
          data-testid="user-panel-toggle-active"
          @click="confirmingToggle = true"
        >
          {{ toggleActiveLabel }}
        </SfButtonDs>
        <span v-if="blockedReason" class="tw-sr-only">
          {{ blockedReason }}
        </span>
      </div>

      <p
        v-if="store.actionError"
        class="tw-m-0 tw-text-sm tw-text-sf-danger"
        role="alert"
        data-testid="user-panel-action-error"
      >
        {{ store.actionError }}
      </p>

      <SfButtonDs
        v-if="isPersisted"
        variant="secondary"
        :disabled="store.busy"
        :loading="store.mailing"
        data-testid="user-panel-mail-action"
        @click="onMailAction"
      >
        {{ mailActionLabel }}
      </SfButtonDs>

      <SfButtonDs
        variant="primary"
        :disabled="store.busy || !store.isDirty"
        :loading="store.status === 'saving'"
        data-testid="user-panel-save"
        @click="onSave"
      >
        {{ I18n.t('user.panel.save_changes') }}
      </SfButtonDs>
    </template>
  </SfDrawer>

  <SfModal
    v-if="confirmingToggle"
    size="s"
    data-testid="user-panel-toggle-active-modal"
    @close="confirmingToggle = false"
  >
    <template #title>
      {{ toggleActiveLabel }}
    </template>
    <template #body>
      <p class="tw-m-0 tw-text-center tw-text-sm tw-text-sf-slate-dark-1">
        {{ toggleActiveConfirmation }}
      </p>
    </template>
    <template #footer>
      <div class="tw-flex tw-justify-center tw-gap-2">
        <SfButtonDs variant="secondary" @click="confirmingToggle = false">
          {{ I18n.t('common.cancel') }}
        </SfButtonDs>
        <SfButtonDs
          variant="danger"
          :loading="store.toggling"
          :disabled="store.toggling"
          data-testid="user-panel-toggle-active-confirm"
          @click="onToggleActive"
        >
          {{ toggleActiveLabel }}
        </SfButtonDs>
      </div>
    </template>
  </SfModal>

  <DiscardChangesModal
    v-if="confirmingDiscard"
    @keep="confirmingDiscard = false"
    @discard="discard"
  />
</template>

<script setup lang="ts">
import type { UserPanelActionResult } from '@app/application/user-management/types'

import { computed, ref, watch } from 'vue'

import {
  PANEL_SKELETON_ROWS,
  QUERY_KEYS,
  STATUS_VARIANTS,
} from '@app/application/user-management/constants'
import { footerMailActions } from '@app/application/user-management/types'
import { useUserPanelStore } from '@app/application/user-management/stores/user-panel'
import { toRecordId } from '@app/application/user-management/utils/list-query'
import useI18n from '@app/shared/vue/composables/use-I18n'
import usePanelToast from '@app/application/user-management/composables/use-panel-toast'
import useDiscardGuard from '@app/application/user-management/composables/use-discard-guard'
import { SfButtonDs } from '@silverfin/ui-components'
import SfDrawer from '@app/shared/vue/components/sf-drawer/sf-drawer.vue'
import SfModal from '@app/shared/vue/components/sf-modal/sf-modal.vue'
import SfSkeletonLoader from '@app/shared/vue/components/sf-skeleton-loader.vue'
import SfStaticPill from '@app/shared/vue/components/sf-static-pill/sf-static-pill.vue'
import DiscardChangesModal from '@app/application/user-management/components/discard-changes-modal.vue'
import useUrlStorage from '@app/shared/vue/composables/use-url-storage'
import AuthenticationSection from '@app/application/user-management/components/user-panel/sections/authentication-section.vue'
import EmailAddressSection from '@app/application/user-management/components/user-panel/sections/email-address-section.vue'
import ExternalRolesSection from '@app/application/user-management/components/user-panel/sections/external-roles-section.vue'
import FileAccessSection from '@app/application/user-management/components/user-panel/sections/file-access-section.vue'
import PulseAccessSection from '@app/application/user-management/components/user-panel/sections/pulse-access-section.vue'
import RolesSection from '@app/application/user-management/components/user-panel/sections/roles-section.vue'
import UserDetailsSection from '@app/application/user-management/components/user-panel/sections/user-details-section.vue'

defineOptions({ name: 'UserDetailPanel' })

const emit = defineEmits<{ refresh: [] }>()

const I18n = useI18n()
const store = useUserPanelStore()
const raiseToast = usePanelToast()

const linkedUserId = useUrlStorage<number | null>(
  QUERY_KEYS.USER,
  null,
  (id) => (id === null ? '' : String(id)),
  { deserialize: toRecordId }
)

// The sections that grant access hide themselves for a deactivated user, so the
// panel says why the space is empty, as `form.html.slim` does.
const deactivated = computed(() => store.user !== null && !store.user.isActive)

const title = computed(() => {
  if (!store.creating) return store.user?.name ?? ''

  return store.user?.external
    ? I18n.t('user.panel.new_external')
    : I18n.t('user.panel.new_staff')
})

const confirmingToggle = ref(false)
const { confirmingDiscard, requestClose, discard } = useDiscardGuard(
  () => store.isDirty && store.status !== 'saving',
  () => store.close()
)

// A user the panel has not saved yet has nothing to deactivate and no address
// to mail.
const isPersisted = computed(() => store.openUserId !== null)

const blockedReason = computed(() => store.capabilities.deactivateBlockedReason)

const toggleActiveLabel = computed(() =>
  store.user?.isActive
    ? I18n.t('user.panel.deactivate')
    : I18n.t('user.panel.reactivate')
)

const toggleActiveConfirmation = computed(() => {
  const userName = store.user?.name ?? ''

  return store.user?.isActive
    ? I18n.t('user.panel.deactivate_confirm', { user_name: userName })
    : I18n.t('user.panel.reactivate_confirm', { user_name: userName })
})

const mailActionLabel = computed(() =>
  store.capabilities.footerMailAction === footerMailActions.WELCOME_MAIL
    ? I18n.t('user.send_welcome_mail')
    : I18n.t('user.password_reset')
)

const onActionResult = (result: UserPanelActionResult | null): void => {
  if (!result) {
    if (!store.actionError) {
      raiseToast(I18n.t('user.panel.action_error'), 'danger')
    }

    return
  }

  if (result.message) raiseToast(result.message, 'success')

  emit('refresh')
}

const onToggleActive = async (): Promise<void> => {
  // A second click while the first request runs would otherwise close the
  // confirmation and report the request as failed.
  if (store.toggling) return

  const result = await store.toggleActive()

  confirmingToggle.value = false
  onActionResult(result)
}

const onMailAction = async (): Promise<void> => {
  onActionResult(await store.sendFooterMail())
}

const onSave = async (): Promise<void> => {
  const wasCreating = store.creating
  const name = store.draft?.name ?? ''
  const outcome = await store.save()

  if (outcome === 'failed') {
    if (!store.hasErrors) {
      raiseToast(I18n.t('user.panel.save_error'), 'danger')
    }

    return
  }

  // A stale save answers after the panel closed or opened again, so the panel
  // open now is not the one that saved.
  const stillOpen = outcome === 'saved'

  if (wasCreating) {
    if (stillOpen) store.close()
    raiseToast(I18n.t('user.panel.create_success', { name }), 'success')
    emit('refresh')

    return
  }

  if (store.warning) {
    raiseToast(store.warning, 'warning')
  } else {
    raiseToast(I18n.t('user.flash_message_for_successful_update'), 'success')
  }

  if (stillOpen) store.close()
  emit('refresh')
}

watch(
  linkedUserId,
  (id) => {
    if (id === store.openUserId) return

    if (id === null) {
      store.close()
    } else {
      store.open(id)
    }
  },
  { immediate: true }
)

watch(
  () => store.openUserId,
  (id) => {
    linkedUserId.value = id
  }
)
</script>
